import type { Account, Prisma, PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { HttpError } from '../../core/errors.js';
import { isoOf, toDate } from '../sales/documents.service.js';

type Db = PrismaClient | Prisma.TransactionClient;
const D = (v: Decimal.Value | { toString(): string } | null | undefined) =>
  new Decimal((v ?? 0).toString());
const fx = (v: Decimal) => v.toFixed(0);

export const iso = (d: Date) => d.toISOString().slice(0, 10);

export function parseRange(
  q: { from?: unknown; to?: unknown },
  defaults?: { from: string; to: string },
) {
  const today = new Date().toISOString().slice(0, 10);
  const to = typeof q.to === 'string' && q.to ? q.to : (defaults?.to ?? today);
  const from =
    typeof q.from === 'string' && q.from ? q.from : (defaults?.from ?? `${to.slice(0, 4)}-01-01`);
  for (const v of [from, to])
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(Date.parse(`${v}T00:00:00Z`)))
      throw new HttpError(400, 'VALIDATION_ERROR', 'Dates must be YYYY-MM-DD');
  if (from > to)
    throw new HttpError(400, 'VALIDATION_ERROR', 'The start date is after the end date');
  return { from, to };
}

const shiftYear = (isoDate: string, years: number) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return iso(d);
};
const addDaysIso = (isoDate: string, days: number) =>
  iso(new Date(new Date(`${isoDate}T00:00:00Z`).getTime() + days * 86_400_000));
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

// ───────────────────────── income statement ─────────────────────────

export interface StatementLine {
  accountId: number;
  code: string;
  name: string;
  amount: string;
  compare?: string;
}
export interface IncomeStatement {
  basis: 'accrual';
  period: { from: string; to: string };
  comparePeriod: { from: string; to: string } | null;
  sections: {
    revenue: StatementLine[];
    costOfSales: StatementLine[];
    operatingExpenses: StatementLine[];
    otherIncome: StatementLine[];
    otherExpenses: StatementLine[];
  };
  totals: Record<
    | 'revenue'
    | 'costOfSales'
    | 'grossProfit'
    | 'operatingExpenses'
    | 'operatingProfit'
    | 'otherIncome'
    | 'otherExpenses'
    | 'netProfit',
    { amount: string; compare?: string }
  >;
}

async function plMovements(db: Db, companyId: number, from: string, to: string) {
  const rows = await db.journalLine.groupBy({
    by: ['accountId'],
    where: {
      entry: {
        companyId,
        status: 'posted',
        // Year-end closing entries move profit into retained earnings; they are not trading.
        sourceType: { not: 'closing' },
        entryDate: { gte: toDate(from), lte: toDate(to) },
      },
      account: { type: { in: ['revenue', 'expense'] } },
    },
    _sum: { debit: true, credit: true },
  });
  return new Map(
    rows.map((r) => [r.accountId, { debit: D(r._sum.debit), credit: D(r._sum.credit) }]),
  );
}

export async function incomeStatement(
  db: Db,
  companyId: number,
  opts: { from: string; to: string; compare?: 'none' | 'previous' | 'lastyear' },
): Promise<IncomeStatement> {
  const accounts = await db.account.findMany({
    where: { companyId, type: { in: ['revenue', 'expense'] } },
    orderBy: { code: 'asc' },
  });
  let comparePeriod: IncomeStatement['comparePeriod'] = null;
  if (opts.compare === 'previous') {
    const len = daysBetween(opts.from, opts.to) + 1;
    comparePeriod = { from: addDaysIso(opts.from, -len), to: addDaysIso(opts.from, -1) };
  } else if (opts.compare === 'lastyear') {
    comparePeriod = { from: shiftYear(opts.from, -1), to: shiftYear(opts.to, -1) };
  }
  const cur = await plMovements(db, companyId, opts.from, opts.to);
  const prev = comparePeriod
    ? await plMovements(db, companyId, comparePeriod.from, comparePeriod.to)
    : null;
  const amountOf = (a: Account, m: Map<number, { debit: Decimal; credit: Decimal }>) => {
    const x = m.get(a.id);
    if (!x) return new Decimal(0);
    return a.type === 'revenue' ? x.credit.minus(x.debit) : x.debit.minus(x.credit);
  };
  const bucket = (pick: (a: Account) => boolean) => {
    const lines: StatementLine[] = [];
    let total = new Decimal(0);
    let totalPrev = new Decimal(0);
    for (const a of accounts.filter(pick)) {
      const amt = amountOf(a, cur);
      const cmp = prev ? amountOf(a, prev) : null;
      if (amt.isZero() && (!cmp || cmp.isZero())) continue;
      total = total.plus(amt);
      if (cmp) totalPrev = totalPrev.plus(cmp);
      lines.push({
        accountId: a.id,
        code: a.code,
        name: a.name,
        amount: fx(amt),
        ...(cmp ? { compare: fx(cmp) } : {}),
      });
    }
    return { lines, total, totalPrev };
  };
  const rev = bucket((a) => a.type === 'revenue' && a.subtype !== 'other');
  const cogs = bucket((a) => a.type === 'expense' && a.subtype === 'cogs');
  const opex = bucket((a) => a.type === 'expense' && a.subtype !== 'cogs' && a.subtype !== 'other');
  const oi = bucket((a) => a.type === 'revenue' && a.subtype === 'other');
  const oe = bucket((a) => a.type === 'expense' && a.subtype === 'other');
  const t = (cur: Decimal, cmp: Decimal) => ({
    amount: fx(cur),
    ...(prev ? { compare: fx(cmp) } : {}),
  });
  const gross = rev.total.minus(cogs.total);
  const grossP = rev.totalPrev.minus(cogs.totalPrev);
  const op = gross.minus(opex.total);
  const opP = grossP.minus(opex.totalPrev);
  const net = op.plus(oi.total).minus(oe.total);
  const netP = opP.plus(oi.totalPrev).minus(oe.totalPrev);
  return {
    basis: 'accrual',
    period: { from: opts.from, to: opts.to },
    comparePeriod,
    sections: {
      revenue: rev.lines,
      costOfSales: cogs.lines,
      operatingExpenses: opex.lines,
      otherIncome: oi.lines,
      otherExpenses: oe.lines,
    },
    totals: {
      revenue: t(rev.total, rev.totalPrev),
      costOfSales: t(cogs.total, cogs.totalPrev),
      grossProfit: t(gross, grossP),
      operatingExpenses: t(opex.total, opex.totalPrev),
      operatingProfit: t(op, opP),
      otherIncome: t(oi.total, oi.totalPrev),
      otherExpenses: t(oe.total, oe.totalPrev),
      netProfit: t(net, netP),
    },
  };
}

// ───────────────────────── balance sheet ─────────────────────────

export interface BalanceSheet {
  asOf: string;
  assets: { group: string; lines: StatementLine[]; total: string }[];
  liabilities: { group: string; lines: StatementLine[]; total: string }[];
  equity: { lines: StatementLine[]; currentEarnings: string; total: string };
  totals: { assets: string; liabilities: string; equity: string; liabilitiesAndEquity: string };
  balanced: boolean;
}

const GROUP_LABEL: Record<string, string> = {
  cash: 'Cash and bank',
  bank: 'Cash and bank',
  mobile: 'Cash and bank',
  receivable: 'Receivables',
  tax: 'Tax accounts',
  inventory: 'Inventory',
  fixed: 'Fixed assets',
  payable: 'Payables',
  advances: 'Customer advances',
  current: 'Current liabilities',
};

export async function balanceSheet(db: Db, companyId: number, asOf: string): Promise<BalanceSheet> {
  const rows = await db.journalLine.groupBy({
    by: ['accountId'],
    where: { entry: { companyId, status: 'posted', entryDate: { lte: toDate(asOf) } } },
    _sum: { debit: true, credit: true },
  });
  const bal = new Map(rows.map((r) => [r.accountId, D(r._sum.debit).minus(D(r._sum.credit))]));
  const accounts = await db.account.findMany({ where: { companyId }, orderBy: { code: 'asc' } });
  const group = (type: 'asset' | 'liability') => {
    const groups = new Map<string, { lines: StatementLine[]; total: Decimal }>();
    for (const a of accounts.filter((x) => x.type === type)) {
      const raw = bal.get(a.id) ?? new Decimal(0);
      const amt = type === 'asset' ? raw : raw.negated();
      if (amt.isZero()) continue;
      const g = a.isCash
        ? 'Cash and bank'
        : (GROUP_LABEL[a.subtype ?? ''] ??
          (type === 'asset' ? 'Other assets' : 'Other liabilities'));
      const entry = groups.get(g) ?? { lines: [], total: new Decimal(0) };
      entry.lines.push({ accountId: a.id, code: a.code, name: a.name, amount: fx(amt) });
      entry.total = entry.total.plus(amt);
      groups.set(g, entry);
    }
    return [...groups.entries()].map(([g, v]) => ({
      group: g,
      lines: v.lines,
      total: fx(v.total),
    }));
  };
  const assets = group('asset');
  const liabilities = group('liability');
  const eqLines: StatementLine[] = [];
  let eq = new Decimal(0);
  for (const a of accounts.filter((x) => x.type === 'equity')) {
    const amt = (bal.get(a.id) ?? new Decimal(0)).negated();
    if (amt.isZero()) continue;
    eq = eq.plus(amt);
    eqLines.push({ accountId: a.id, code: a.code, name: a.name, amount: fx(amt) });
  }
  // Profit not yet closed into retained earnings (closing entries zero the P&L accounts).
  let earnings = new Decimal(0);
  for (const a of accounts.filter((x) => x.type === 'revenue' || x.type === 'expense'))
    earnings = earnings.minus(bal.get(a.id) ?? 0);
  const sum = (g: { total: string }[]) => g.reduce((s, x) => s.plus(x.total), new Decimal(0));
  const tA = sum(assets);
  const tL = sum(liabilities);
  const tE = eq.plus(earnings);
  return {
    asOf,
    assets,
    liabilities,
    equity: { lines: eqLines, currentEarnings: fx(earnings), total: fx(tE) },
    totals: {
      assets: fx(tA),
      liabilities: fx(tL),
      equity: fx(tE),
      liabilitiesAndEquity: fx(tL.plus(tE)),
    },
    balanced: tA.equals(tL.plus(tE)),
  };
}

// ───────────────────────── cash flow (direct method) ─────────────────────────

export const CASH_BUCKETS = [
  ['customers', 'Receipts from customers', 'operating'],
  ['otherReceipts', 'Other operating receipts', 'operating'],
  ['suppliers', 'Payments to suppliers', 'operating'],
  ['expenses', 'Operating expenses paid', 'operating'],
  ['taxes', 'Taxes paid (VAT, WHT, income tax)', 'operating'],
  ['otherPayments', 'Other operating payments', 'operating'],
  ['investing', 'Investing activities', 'investing'],
  ['financing', 'Financing activities', 'financing'],
] as const;
export type CashBucket = (typeof CASH_BUCKETS)[number][0];

export interface CashFlow {
  period: { from: string; to: string };
  openingCash: string;
  sections: {
    operating: { key: CashBucket; label: string; amount: string }[];
    investing: { key: CashBucket; label: string; amount: string }[];
    financing: { key: CashBucket; label: string; amount: string }[];
  };
  totals: { operating: string; investing: string; financing: string; netChange: string };
  closingCash: string;
  /** Sum of all cash-type account balances on the end date: must equal closingCash. */
  cashAccountsBalance: string;
  reconciled: boolean;
  accounts: { accountId: number; code: string; name: string; balance: string }[];
}

const TAX_KEYS = new Set(['VAT_IN', 'VAT_OUT', 'WHT_PAY', 'WHT_REC']);

/**
 * Direct method. Every entry that moves cash is split by its NON-cash lines (their credit minus debit is
 * exactly the cash they explain), so internal transfers between cash accounts net to zero by construction.
 * Receipts and payouts keep their withholding and FX lines inside customers/suppliers (net figures).
 */
export async function cashFlow(
  db: Db,
  companyId: number,
  opts: { from: string; to: string },
): Promise<CashFlow> {
  const accounts = await db.account.findMany({ where: { companyId } });
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const cashIds = accounts.filter((a) => a.isCash).map((a) => a.id);
  const balanceAt = async (asOf: Date | null) => {
    const rows = await db.journalLine.groupBy({
      by: ['accountId'],
      where: {
        accountId: { in: cashIds },
        entry: { companyId, status: 'posted', ...(asOf ? { entryDate: { lte: asOf } } : {}) },
      },
      _sum: { debit: true, credit: true },
    });
    return new Map(rows.map((r) => [r.accountId, D(r._sum.debit).minus(D(r._sum.credit))]));
  };
  const before = await balanceAt(new Date(toDate(opts.from).getTime() - 86_400_000));
  const opening = [...before.values()].reduce((s, v) => s.plus(v), new Decimal(0));

  const entries = await db.journalEntry.findMany({
    where: {
      companyId,
      status: 'posted',
      entryDate: { gte: toDate(opts.from), lte: toDate(opts.to) },
      lines: { some: { accountId: { in: cashIds } } },
    },
    include: { lines: true },
  });
  const reversed = await db.journalEntry.findMany({
    where: { id: { in: entries.map((e) => e.reversesId).filter((x): x is number => !!x) } },
    select: { id: true, sourceType: true },
  });
  const origType = new Map(reversed.map((r) => [r.id, r.sourceType]));

  const totals = new Map<CashBucket, Decimal>(CASH_BUCKETS.map(([k]) => [k, new Decimal(0)]));
  const add = (k: CashBucket, v: Decimal) => totals.set(k, totals.get(k)!.plus(v));
  for (const e of entries) {
    const source = e.reversesId ? (origType.get(e.reversesId) ?? 'manual') : e.sourceType;
    for (const l of e.lines) {
      const a = byId.get(l.accountId)!;
      if (a.isCash) continue; // cash lines are the result, not the explanation
      const cash = D(l.credit).minus(D(l.debit)); // cash effect attributed to this counter-line
      let key: CashBucket;
      if (source === 'receipt') key = 'customers';
      else if (source === 'payout') key = 'suppliers';
      else if (source === 'expense' || source === 'transfer') key = 'expenses';
      else if (a.systemKey === 'AR' || a.systemKey === 'CUST_ADV') key = 'customers';
      else if (a.systemKey === 'AP') key = 'suppliers';
      else if (a.systemKey && TAX_KEYS.has(a.systemKey)) key = 'taxes';
      else if (a.cashFlowCategory === 'investing') key = 'investing';
      else if (a.cashFlowCategory === 'financing' || a.type === 'equity') key = 'financing';
      else if (a.type === 'expense') key = 'expenses';
      else if (a.type === 'revenue') key = 'otherReceipts';
      else key = cash.gte(0) ? 'otherReceipts' : 'otherPayments';
      add(key, cash);
    }
  }
  const section = (cat: 'operating' | 'investing' | 'financing') =>
    CASH_BUCKETS.filter((b) => b[2] === cat).map(([key, label]) => ({
      key,
      label,
      amount: fx(totals.get(key)!),
    }));
  const sum = (cat: 'operating' | 'investing' | 'financing') =>
    CASH_BUCKETS.filter((b) => b[2] === cat).reduce(
      (s, [k]) => s.plus(totals.get(k)!),
      new Decimal(0),
    );
  const op = sum('operating');
  const inv = sum('investing');
  const fin = sum('financing');
  const net = op.plus(inv).plus(fin);
  const closing = opening.plus(net);
  const end = await balanceAt(toDate(opts.to));
  const cashTotal = [...end.values()].reduce((s, v) => s.plus(v), new Decimal(0));
  return {
    period: { from: opts.from, to: opts.to },
    openingCash: fx(opening),
    sections: {
      operating: section('operating'),
      investing: section('investing'),
      financing: section('financing'),
    },
    totals: { operating: fx(op), investing: fx(inv), financing: fx(fin), netChange: fx(net) },
    closingCash: fx(closing),
    cashAccountsBalance: fx(cashTotal),
    reconciled: closing.equals(cashTotal),
    accounts: accounts
      .filter((a) => a.isCash)
      .map((a) => ({
        accountId: a.id,
        code: a.code,
        name: a.name,
        balance: fx(end.get(a.id) ?? new Decimal(0)),
      })),
  };
}

// ───────────────────────── VAT and WHT ─────────────────────────

export interface VatSummary {
  period: { from: string; to: string };
  sales: { vatCode: string; net: string; vat: string }[];
  purchases: { vatCode: string; net: string; vat: string }[];
  outputVat: string;
  inputVat: string;
  netVatPayable: string;
  ledger: { outputVat: string; inputVat: string };
  reconciled: boolean;
}

export async function vatSummary(
  db: Db,
  companyId: number,
  from: string,
  to: string,
): Promise<VatSummary> {
  const range = { gte: toDate(from), lte: toDate(to) };
  const sales = new Map<string, { net: Decimal; vat: Decimal }>();
  const purchases = new Map<string, { net: Decimal; vat: Decimal }>();
  const bump = (m: typeof sales, code: string, net: Decimal, vat: Decimal) => {
    const x = m.get(code) ?? { net: new Decimal(0), vat: new Decimal(0) };
    m.set(code, { net: x.net.plus(net), vat: x.vat.plus(vat) });
  };
  const docs = await db.salesDocument.findMany({
    where: {
      companyId,
      docType: { in: ['invoice', 'creditnote'] },
      journalEntryId: { not: null },
      issueDate: range,
    },
    include: { lines: true },
  });
  // Voided documents are reversed in the ledger; only their reversal date matters, so they are excluded
  // here and the ledger comparison below flags any period mismatch.
  for (const d of docs) {
    if (d.status === 'void') continue;
    const sign = d.docType === 'creditnote' ? -1 : 1;
    for (const l of d.lines)
      if (l.kind === 'item' && !(l.isOptional && !l.isSelected))
        bump(sales, l.vatCode, D(l.baseNet).times(sign), D(l.baseTax).times(sign));
  }
  const bills = await db.vendorBill.findMany({
    where: { companyId, journalEntryId: { not: null }, status: { not: 'void' }, issueDate: range },
    include: { lines: true },
  });
  for (const b of bills)
    for (const l of b.lines)
      bump(
        purchases,
        l.vatCode,
        D(l.lineNet).times(b.exchangeRate.toString()).toDecimalPlaces(0, Decimal.ROUND_HALF_UP),
        D(l.lineTax).times(b.exchangeRate.toString()).toDecimalPlaces(0, Decimal.ROUND_HALF_UP),
      );
  const exps = await db.expense.findMany({
    where: { companyId, status: { not: 'void' }, expenseDate: range },
  });
  for (const e of exps)
    bump(
      purchases,
      e.vatCode,
      D(e.amount).times(e.exchangeRate.toString()).toDecimalPlaces(0, Decimal.ROUND_HALF_UP),
      D(e.vatAmount).times(e.exchangeRate.toString()).toDecimalPlaces(0, Decimal.ROUND_HALF_UP),
    );
  const out = [...sales.values()].reduce((s, x) => s.plus(x.vat), new Decimal(0));
  const inn = [...purchases.values()].reduce((s, x) => s.plus(x.vat), new Decimal(0));

  const keyed = await db.account.findMany({
    where: { companyId, systemKey: { in: ['VAT_OUT', 'VAT_IN'] } },
  });
  const mov = await db.journalLine.groupBy({
    by: ['accountId'],
    where: {
      accountId: { in: keyed.map((k) => k.id) },
      entry: { companyId, status: 'posted', entryDate: range },
    },
    _sum: { debit: true, credit: true },
  });
  const net = (key: string) => {
    const acc = keyed.find((k) => k.systemKey === key);
    const m = mov.find((r) => r.accountId === acc?.id);
    return m ? D(m._sum.debit).minus(D(m._sum.credit)) : new Decimal(0);
  };
  const ledgerOut = net('VAT_OUT').negated();
  const ledgerIn = net('VAT_IN');
  const list = (m: typeof sales) =>
    [...m.entries()].map(([vatCode, v]) => ({ vatCode, net: fx(v.net), vat: fx(v.vat) }));
  return {
    period: { from, to },
    sales: list(sales),
    purchases: list(purchases),
    outputVat: fx(out),
    inputVat: fx(inn),
    netVatPayable: fx(out.minus(inn)),
    ledger: { outputVat: fx(ledgerOut), inputVat: fx(ledgerIn) },
    reconciled: ledgerOut.equals(out) && ledgerIn.equals(inn),
  };
}

export interface WhtReport {
  period: { from: string; to: string };
  withheldByUs: {
    payoutNo: string;
    date: string;
    vendor: string;
    currency: string;
    amount: string;
    base: string;
    payoutId: number;
  }[];
  withheldFromUs: {
    receiptNo: string;
    date: string;
    client: string;
    currency: string;
    amount: string;
    base: string;
    receiptId: number;
  }[];
  totals: { withheldByUs: string; withheldFromUs: string };
  ledger: { payable: string; receivable: string };
  reconciled: boolean;
}

export async function whtReport(
  db: Db,
  companyId: number,
  from: string,
  to: string,
): Promise<WhtReport> {
  const range = { gte: toDate(from), lte: toDate(to) };
  const payouts = await db.payoutReceipt.findMany({
    where: { companyId, status: 'posted', whtWithheld: { gt: 0 }, payoutDate: range },
    orderBy: { payoutDate: 'asc' },
  });
  const receipts = await db.paymentReceipt.findMany({
    where: { companyId, status: 'posted', whtWithheld: { gt: 0 }, receiptDate: range },
    orderBy: { receiptDate: 'asc' },
  });
  const parties = new Map(
    (
      await db.party.findMany({
        where: {
          id: { in: [...payouts.map((p) => p.partyId), ...receipts.map((r) => r.partyId)] },
        },
        select: { id: true, name: true },
      })
    ).map((p) => [p.id, p.name]),
  );
  const base = (amt: Prisma.Decimal, rate: Prisma.Decimal) =>
    D(amt).times(rate.toString()).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  const byUs = payouts.map((p) => ({
    payoutId: p.id,
    payoutNo: p.payoutNo,
    date: isoOf(p.payoutDate)!,
    vendor: parties.get(p.partyId) ?? '',
    currency: p.currency,
    amount: p.whtWithheld.toFixed(4),
    base: fx(base(p.whtWithheld, p.exchangeRate)),
  }));
  const fromUs = receipts.map((r) => ({
    receiptId: r.id,
    receiptNo: r.receiptNo,
    date: isoOf(r.receiptDate)!,
    client: parties.get(r.partyId) ?? '',
    currency: r.currency,
    amount: r.whtWithheld.toFixed(4),
    base: fx(base(r.whtWithheld, r.exchangeRate)),
  }));
  const tUs = byUs.reduce((s, x) => s.plus(x.base), new Decimal(0));
  const tFrom = fromUs.reduce((s, x) => s.plus(x.base), new Decimal(0));
  const keyed = await db.account.findMany({
    where: { companyId, systemKey: { in: ['WHT_PAY', 'WHT_REC'] } },
  });
  const mov = await db.journalLine.groupBy({
    by: ['accountId'],
    where: {
      accountId: { in: keyed.map((k) => k.id) },
      entry: { companyId, status: 'posted', entryDate: range },
    },
    _sum: { debit: true, credit: true },
  });
  const m = (key: string) => {
    const acc = keyed.find((k) => k.systemKey === key);
    const r = mov.find((x) => x.accountId === acc?.id);
    return r ? D(r._sum.debit).minus(D(r._sum.credit)) : new Decimal(0);
  };
  // Remittances to URA are debits to WHT Payable; the report counts what was withheld (credits).
  const payableCredits = await db.journalLine.aggregate({
    where: {
      accountId: keyed.find((k) => k.systemKey === 'WHT_PAY')?.id ?? -1,
      entry: { companyId, status: 'posted', entryDate: range },
    },
    _sum: { credit: true },
  });
  const recvDebits = await db.journalLine.aggregate({
    where: {
      accountId: keyed.find((k) => k.systemKey === 'WHT_REC')?.id ?? -1,
      entry: { companyId, status: 'posted', entryDate: range },
    },
    _sum: { debit: true },
  });
  void m;
  return {
    period: { from, to },
    withheldByUs: byUs,
    withheldFromUs: fromUs,
    totals: { withheldByUs: fx(tUs), withheldFromUs: fx(tFrom) },
    ledger: {
      payable: fx(D(payableCredits._sum.credit)),
      receivable: fx(D(recvDebits._sum.debit)),
    },
    reconciled: D(payableCredits._sum.credit).equals(tUs) && D(recvDebits._sum.debit).equals(tFrom),
  };
}

// ───────────────────────── statements ─────────────────────────

export interface Statement {
  kind: 'client' | 'vendor';
  party: { id: number; name: string; tin: string | null };
  currency: string;
  period: { from: string; to: string };
  opening: string;
  rows: {
    date: string;
    type: string;
    number: string;
    description: string;
    debit: string;
    credit: string;
    balance: string;
    link: { type: string; id: number };
  }[];
  closing: string;
  totalDebits: string;
  totalCredits: string;
}

/**
 * Client statement: what they owe us, in ONE currency (documents keep their own currency; mixing
 * currencies in a running balance would be meaningless). Invoices add, credit notes and receipts reduce.
 */
export async function clientStatement(
  db: Db,
  companyId: number,
  partyId: number,
  opts: { from: string; to: string; currency?: string },
): Promise<Statement> {
  const party = await db.party.findFirst({ where: { id: partyId, companyId } });
  if (!party) throw new HttpError(404, 'NOT_FOUND', 'Client not found');
  const currency = opts.currency ?? party.currency;
  type Ev = {
    date: string;
    type: string;
    number: string;
    description: string;
    amount: Decimal;
    link: { type: string; id: number };
    sort: number;
  };
  const events: Ev[] = [];
  const docs = await db.salesDocument.findMany({
    where: {
      companyId,
      partyId,
      currency,
      docType: { in: ['invoice', 'creditnote'] },
      journalEntryId: { not: null },
    },
  });
  for (const d of docs)
    events.push({
      date: isoOf(d.issueDate)!,
      type: d.docType === 'invoice' ? 'Invoice' : 'Credit note',
      number: d.number ?? '',
      description: d.reference ?? '',
      amount: d.docType === 'invoice' ? D(d.total) : D(d.total).negated(),
      link: { type: d.docType, id: d.id },
      sort: 0,
    });
  const voided = docs.filter((d) => d.status === 'void');
  for (const d of voided)
    events.push({
      date: isoOf(d.voidedAt ?? d.issueDate)!,
      type: `Void ${d.docType === 'invoice' ? 'invoice' : 'credit note'}`,
      number: d.number ?? '',
      description: d.voidReason ?? '',
      amount: d.docType === 'invoice' ? D(d.total).negated() : D(d.total),
      link: { type: d.docType, id: d.id },
      sort: 1,
    });
  const receipts = await db.paymentReceipt.findMany({ where: { companyId, partyId, currency } });
  for (const r of receipts) {
    const settled = D(r.allocatedAmount).plus(D(r.advanceAmount));
    // The client's account is credited for everything settled: cash plus tax they withheld.
    events.push({
      date: isoOf(r.receiptDate)!,
      type: 'Payment received',
      number: r.receiptNo,
      description: D(r.whtWithheld).gt(0)
        ? `Includes ${r.whtWithheld.toFixed(0)} tax withheld`
        : (r.reference ?? ''),
      amount: settled.negated(),
      link: { type: 'receipt', id: r.id },
      sort: 2,
    });
    if (r.status === 'void' && r.voidedAt)
      events.push({
        date: isoOf(r.voidedAt)!,
        type: 'Void receipt',
        number: r.receiptNo,
        description: r.voidReason ?? '',
        amount: settled,
        link: { type: 'receipt', id: r.id },
        sort: 3,
      });
  }
  events.sort((a, b) => a.date.localeCompare(b.date) || a.sort - b.sort);
  const opening = events
    .filter((e) => e.date < opts.from)
    .reduce((s, e) => s.plus(e.amount), new Decimal(0));
  let running = opening;
  const rows = events
    .filter((e) => e.date >= opts.from && e.date <= opts.to)
    .map((e) => {
      running = running.plus(e.amount);
      return {
        date: e.date,
        type: e.type,
        number: e.number,
        description: e.description,
        debit: e.amount.gt(0) ? e.amount.toFixed(4) : '',
        credit: e.amount.lt(0) ? e.amount.negated().toFixed(4) : '',
        balance: running.toFixed(4),
        link: e.link,
      };
    });
  const totalD = rows.reduce((s, r) => s.plus(r.debit || 0), new Decimal(0));
  const totalC = rows.reduce((s, r) => s.plus(r.credit || 0), new Decimal(0));
  return {
    kind: 'client',
    party: { id: party.id, name: party.name, tin: party.tin },
    currency,
    period: { from: opts.from, to: opts.to },
    opening: opening.toFixed(4),
    rows,
    closing: running.toFixed(4),
    totalDebits: totalD.toFixed(4),
    totalCredits: totalC.toFixed(4),
  };
}

/** Vendor statement: what we owe them. Bills and staff expenses add, payouts reduce. */
export async function vendorStatement(
  db: Db,
  companyId: number,
  partyId: number,
  opts: { from: string; to: string; currency?: string },
): Promise<Statement> {
  const party = await db.party.findFirst({ where: { id: partyId, companyId, isVendor: true } });
  if (!party) throw new HttpError(404, 'NOT_FOUND', 'Vendor not found');
  const currency = opts.currency ?? party.currency;
  type Ev = {
    date: string;
    type: string;
    number: string;
    description: string;
    amount: Decimal;
    link: { type: string; id: number };
    sort: number;
  };
  const events: Ev[] = [];
  const bills = await db.vendorBill.findMany({
    where: { companyId, partyId, currency, journalEntryId: { not: null } },
  });
  for (const b of bills) {
    events.push({
      date: isoOf(b.issueDate)!,
      type: 'Bill',
      number: b.number ?? '',
      description: b.vendorRef ?? '',
      amount: D(b.total),
      link: { type: 'bill', id: b.id },
      sort: 0,
    });
    if (b.status === 'void' && b.voidedAt)
      events.push({
        date: isoOf(b.voidedAt)!,
        type: 'Void bill',
        number: b.number ?? '',
        description: b.voidReason ?? '',
        amount: D(b.total).negated(),
        link: { type: 'bill', id: b.id },
        sort: 1,
      });
  }
  const exps = await db.expense.findMany({
    where: { companyId, partyId, currency, paidBy: 'staff' },
  });
  for (const e of exps) {
    events.push({
      date: isoOf(e.expenseDate)!,
      type: 'Expense claim',
      number: e.number,
      description: e.description,
      amount: D(e.total),
      link: { type: 'expense', id: e.id },
      sort: 0,
    });
    if (e.status === 'void' && e.voidedAt)
      events.push({
        date: isoOf(e.voidedAt)!,
        type: 'Void expense',
        number: e.number,
        description: e.voidReason ?? '',
        amount: D(e.total).negated(),
        link: { type: 'expense', id: e.id },
        sort: 1,
      });
  }
  const payouts = await db.payoutReceipt.findMany({ where: { companyId, partyId, currency } });
  for (const p of payouts) {
    const settled = D(p.allocatedAmount);
    events.push({
      date: isoOf(p.payoutDate)!,
      type: 'Payment made',
      number: p.payoutNo,
      description: D(p.whtWithheld).gt(0)
        ? `Includes ${p.whtWithheld.toFixed(0)} tax withheld`
        : (p.reference ?? ''),
      amount: settled.negated(),
      link: { type: 'payout', id: p.id },
      sort: 2,
    });
    if (p.status === 'void' && p.voidedAt)
      events.push({
        date: isoOf(p.voidedAt)!,
        type: 'Void payout',
        number: p.payoutNo,
        description: p.voidReason ?? '',
        amount: settled,
        link: { type: 'payout', id: p.id },
        sort: 3,
      });
  }
  events.sort((a, b) => a.date.localeCompare(b.date) || a.sort - b.sort);
  const opening = events
    .filter((e) => e.date < opts.from)
    .reduce((s, e) => s.plus(e.amount), new Decimal(0));
  let running = opening;
  const rows = events
    .filter((e) => e.date >= opts.from && e.date <= opts.to)
    .map((e) => {
      running = running.plus(e.amount);
      return {
        date: e.date,
        type: e.type,
        number: e.number,
        description: e.description,
        debit: e.amount.lt(0) ? e.amount.negated().toFixed(4) : '',
        credit: e.amount.gt(0) ? e.amount.toFixed(4) : '',
        balance: running.toFixed(4),
        link: e.link,
      };
    });
  const totalD = rows.reduce((s, r) => s.plus(r.debit || 0), new Decimal(0));
  const totalC = rows.reduce((s, r) => s.plus(r.credit || 0), new Decimal(0));
  return {
    kind: 'vendor',
    party: { id: party.id, name: party.name, tin: party.tin },
    currency,
    period: { from: opts.from, to: opts.to },
    opening: opening.toFixed(4),
    rows,
    closing: running.toFixed(4),
    totalDebits: totalD.toFixed(4),
    totalCredits: totalC.toFixed(4),
  };
}
